(function () {
  "use strict";

  class CityFloatingReviews {
    constructor(root) {
      this.root = root;
      this.launcher = root.querySelector(".city-floating-reviews__launcher");
      this.overlay = root.querySelector("[data-floating-overlay]");
      this.panel = root.querySelector(".city-floating-reviews__panel");
      this.closeButton = root.querySelector(".city-floating-reviews__close");
      this.locationButtons = Array.from(root.querySelectorAll("[data-floating-location]"));
      this.summary = root.querySelector("[data-floating-summary]");
      this.status = root.querySelector("[data-floating-status]");
      this.cards = root.querySelector("[data-floating-cards]");
      this.actions = root.querySelector("[data-floating-actions]");
      this.endpointBase = this.safeEndpoint(root.dataset.endpointBase);
      this.defaultLocation = root.dataset.defaultLocation || "penrith";
      this.cache = new Map();
      this.loadSequence = 0;
      this.hasOpened = false;
    }

    init() {
      if (!this.launcher || !this.overlay || !this.panel || !this.endpointBase) return;

      this.launcher.addEventListener("click", () => this.toggle());
      this.closeButton.addEventListener("click", () => this.close());
      this.locationButtons.forEach((button, index) => {
        button.addEventListener("click", () => this.selectLocation(button.dataset.floatingLocation));
        button.addEventListener("keydown", (event) => this.onLocationKeydown(event, index));
      });
      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && !this.overlay.hidden) this.close();
        if (event.key === "Tab" && !this.overlay.hidden) this.trapFocus(event);
      });
      this.overlay.addEventListener("pointerdown", (event) => {
        if (event.target === this.overlay) this.close();
      });

      const refreshCombined = () => this.loadCombinedProof();
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(refreshCombined, { timeout: 4000 });
      } else {
        window.setTimeout(refreshCombined, 2400);
      }
    }

    toggle() {
      if (this.overlay.hidden) this.open();
      else this.close();
    }

    open() {
      this.overlay.hidden = false;
      this.launcher.setAttribute("aria-expanded", "true");
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      if (!this.hasOpened) {
        this.hasOpened = true;
        this.selectLocation(this.defaultLocation);
      }
      this.closeButton.focus({ preventScroll: true });
    }

    close(restoreFocus = true) {
      this.overlay.hidden = true;
      this.launcher.setAttribute("aria-expanded", "false");
      document.body.style.overflow = this.previousBodyOverflow || "";
      if (restoreFocus) this.launcher.focus({ preventScroll: true });
    }

    trapFocus(event) {
      const focusable = Array.from(
        this.panel.querySelectorAll('button:not([disabled]), a[href]:not([hidden]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.hidden && element.getClientRects().length > 0);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onLocationKeydown(event, index) {
      let target = -1;
      if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        target = (index + 1) % this.locationButtons.length;
      } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        target = (index - 1 + this.locationButtons.length) % this.locationButtons.length;
      } else if (event.key === "Home") {
        target = 0;
      } else if (event.key === "End") {
        target = this.locationButtons.length - 1;
      }
      if (target < 0) return;
      event.preventDefault();
      this.locationButtons[target].focus();
      this.locationButtons[target].click();
    }

    async loadCombinedProof() {
      try {
        const locations = ["penrith", "nepean", "kotara", "wetherill"];
        const payloads = await Promise.all(locations.map((key) => this.fetchPayload(key)));
        const proof = payloads.reduce(
          (result, payload) => {
            const count = Math.max(0, Number(payload.totalReviewCount) || 0);
            const rating = Math.max(0, Math.min(5, Number(payload.averageRating) || 0));
            result.count += count;
            result.weighted += rating * count;
            return result;
          },
          { count: 0, weighted: 0 }
        );
        if (proof.count <= 0) return;
        const rating = (proof.weighted / proof.count).toFixed(1);
        this.root.querySelector("[data-floating-combined-rating]").textContent = rating;
        this.root.querySelector("[data-floating-combined-count]").textContent = proof.count.toLocaleString("en-AU");
        this.root.querySelector("[data-floating-panel-rating]").textContent = rating;
        this.root.querySelector("[data-floating-panel-count]").textContent = proof.count.toLocaleString("en-AU");
      } catch (error) {
        console.warn("City Optics combined Google review proof could not be refreshed.", error);
      }
    }

    async selectLocation(key) {
      if (!this.locationButtons.some((button) => button.dataset.floatingLocation === key)) return;
      this.locationButtons.forEach((button) => {
        button.setAttribute("aria-pressed", String(button.dataset.floatingLocation === key));
      });
      const sequence = ++this.loadSequence;
      this.setLoading();
      try {
        const payload = await this.fetchPayload(key);
        if (sequence === this.loadSequence) this.render(payload);
      } catch (error) {
        if (sequence === this.loadSequence) this.renderError();
        console.warn("City Optics Google reviews could not be loaded.", error);
      }
    }

    async fetchPayload(key) {
      const endpoint = this.endpointFor(key);
      if (!endpoint) throw new Error("Invalid reviews endpoint.");
      if (this.cache.has(endpoint)) return this.cache.get(endpoint);
      const response = await fetch(endpoint, {
        credentials: "same-origin",
        headers: { accept: "application/json" }
      });
      if (!response.ok) throw new Error(`Reviews request failed: ${response.status}`);
      const payload = await response.json();
      this.cache.set(endpoint, payload);
      return payload;
    }

    endpointFor(key) {
      if (!["penrith", "nepean", "kotara", "wetherill"].includes(key)) return "";
      const url = new URL(this.endpointBase, window.location.origin);
      url.searchParams.set("location", key);
      return `${url.pathname}${url.search}`;
    }

    setLoading() {
      this.status.textContent = "Loading this store’s Google reviews…";
      this.status.hidden = false;
      this.summary.hidden = true;
      this.cards.hidden = true;
      this.actions.hidden = true;
    }

    render(payload) {
      const reviews = this.selectWrittenReviews(
        Array.isArray(payload.reviews) ? payload.reviews : []
      );
      const rating = Math.max(0, Math.min(5, Number(payload.averageRating) || 0));
      const count = Math.max(0, Number(payload.totalReviewCount) || 0);
      this.summary.querySelector("[data-floating-store]").textContent = String(payload.locationName || "City Optics");
      this.summary.querySelector("[data-floating-rating]").textContent = rating ? rating.toFixed(1) : "—";
      this.summary.querySelector("[data-floating-stars]").textContent = rating ? this.stars(Math.round(rating)) : "";
      this.summary.querySelector("[data-floating-count]").textContent = `${count.toLocaleString("en-AU")} Google reviews`;
      this.summary.hidden = false;

      this.cards.replaceChildren(...reviews.map((review) => this.reviewCard(review)));
      this.cards.hidden = reviews.length === 0;
      this.status.textContent = reviews.length === 0 ? "No written reviews are available to display yet." : "";
      this.status.hidden = reviews.length > 0;

      const readUrl = this.safeGoogleUrl(payload.mapsUrl);
      this.setLink(this.actions.querySelector("[data-floating-read]"), readUrl);
      this.actions.hidden = !readUrl;
    }

    selectWrittenReviews(reviews) {
      return reviews
        .map((review, index) => {
          const comment = String(review?.comment || "").trim();
          const normalisedComment = comment.toLowerCase();
          const timestamp = Date.parse(review?.createTime || "");
          return {
            review,
            index,
            comment,
            timestamp: Number.isFinite(timestamp) ? timestamp : 0,
            isUsefulWritten:
              comment.length >= 10 &&
              comment.split(/\s+/).filter(Boolean).length >= 2 &&
              normalisedComment !== "star rating only"
          };
        })
        .filter((item) => item.isUsefulWritten)
        .sort((a, b) =>
          b.timestamp - a.timestamp ||
          b.comment.length - a.comment.length ||
          a.index - b.index
        )
        .slice(0, 3)
        .map((item) => item.review);
    }

    reviewCard(review) {
      const card = document.createElement("article");
      card.className = "city-floating-reviews__card";

      const rating = Math.max(0, Math.min(5, Math.round(Number(review.rating) || 0)));
      const stars = document.createElement("div");
      stars.className = "city-floating-reviews__card-stars";
      stars.setAttribute("aria-label", `${rating} out of 5 stars`);
      stars.textContent = this.stars(rating);

      const comment = document.createElement("p");
      comment.textContent = String(review.comment || "").trim();

      const reviewer = document.createElement("footer");
      reviewer.className = "city-floating-reviews__reviewer";
      const avatar = document.createElement("span");
      avatar.className = "city-floating-reviews__avatar";
      avatar.setAttribute("aria-hidden", "true");
      const photo = this.safeReviewerPhotoUrl(review.profilePhotoUrl);
      if (photo) {
        const image = document.createElement("img");
        image.src = photo;
        image.alt = "";
        image.width = 38;
        image.height = 38;
        image.loading = "lazy";
        image.decoding = "async";
        image.referrerPolicy = "no-referrer";
        image.addEventListener("error", () => {
          avatar.replaceChildren();
          avatar.textContent = this.initials(review.author);
        }, { once: true });
        avatar.append(image);
      } else {
        avatar.textContent = this.initials(review.author);
      }

      const details = document.createElement("span");
      const author = document.createElement("strong");
      author.textContent = String(review.author || "A Google user");
      details.append(author);
      const date = this.formatDate(review.createTime);
      if (date) {
        const time = document.createElement("time");
        time.dateTime = String(review.createTime);
        time.textContent = date;
        details.append(time);
      }
      reviewer.append(avatar, details);
      card.append(reviewer, stars, comment);
      return card;
    }

    renderError() {
      this.status.textContent = "Reviews are temporarily unavailable. Please try again shortly.";
      this.status.hidden = false;
      this.summary.hidden = true;
      this.cards.hidden = true;
      this.actions.hidden = true;
    }

    setLink(link, url) {
      if (!link) return;
      link.hidden = !url;
      if (url) link.href = url;
    }

    safeEndpoint(value) {
      try {
        const url = new URL(value, window.location.origin);
        return url.origin === window.location.origin && url.pathname === "/apps/city-optics-reviews/reviews"
          ? url.pathname
          : "";
      } catch {
        return "";
      }
    }

    safeGoogleUrl(value) {
      if (!value) return "";
      try {
        const url = new URL(value);
        const host = url.hostname.toLowerCase();
        const allowed = host === "google.com" || host.endsWith(".google.com") || host === "goo.gl" || host.endsWith(".goo.gl");
        return url.protocol === "https:" && allowed ? url.href : "";
      } catch {
        return "";
      }
    }

    safeReviewerPhotoUrl(value) {
      if (!value) return "";
      try {
        const url = new URL(value);
        const host = url.hostname.toLowerCase();
        const allowed = host === "googleusercontent.com" || host.endsWith(".googleusercontent.com") || host === "ggpht.com" || host.endsWith(".ggpht.com");
        return url.protocol === "https:" && allowed ? url.href : "";
      } catch {
        return "";
      }
    }

    stars(rating) {
      return `${"★".repeat(rating)}${"☆".repeat(5 - rating)}`;
    }

    initials(value) {
      const parts = String(value || "").trim().split(/\s+/).filter(Boolean).slice(0, 2);
      return parts.map((part) => part.charAt(0).toUpperCase()).join("") || "G";
    }

    formatDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return new Intl.DateTimeFormat("en-AU", { month: "short", year: "numeric" }).format(date);
    }
  }

  function initialise(scope) {
    scope.querySelectorAll("[data-city-floating-reviews]").forEach((root) => {
      if (root.dataset.initialised === "true") return;
      root.dataset.initialised = "true";
      new CityFloatingReviews(root).init();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialise(document));
  } else {
    initialise(document);
  }
})();
